import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { 
  Phone, PhoneIncoming, PhoneOutgoing, PhoneMissed, Voicemail, 
  Play, Clock, MapPin, Tag, ChevronDown, ChevronUp, RefreshCw,
  Calendar, User, MessageSquare, ExternalLink
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusConfig = {
  completed: { label: 'Answered', color: 'bg-green-100 text-green-800', icon: PhoneIncoming },
  missed: { label: 'Missed', color: 'bg-red-100 text-red-800', icon: PhoneMissed },
  voicemail: { label: 'Voicemail', color: 'bg-purple-100 text-purple-800', icon: Voicemail },
  abandoned: { label: 'Abandoned', color: 'bg-orange-100 text-orange-800', icon: PhoneMissed },
  busy: { label: 'Busy', color: 'bg-yellow-100 text-yellow-800', icon: Phone }
};

const regionConfig = {
  JAX: { name: 'Jacksonville', color: 'bg-blue-100 text-blue-800' },
  ORL: { name: 'Orlando', color: 'bg-purple-100 text-purple-800' },
  TAM: { name: 'Tampa', color: 'bg-green-100 text-green-800' },
  WPB: { name: 'West Palm Beach', color: 'bg-orange-100 text-orange-800' },
  NAP: { name: 'Naples', color: 'bg-cyan-100 text-cyan-800' },
  MIA: { name: 'Miami', color: 'bg-pink-100 text-pink-800' }
};

/**
 * CallHistoryTimeline - Embeddable component for displaying call history
 * Can be used in Contact pages, Job pages, or any context where call history is needed
 * 
 * @param {string} contactId - Filter calls by contact ID
 * @param {string} jobId - Filter calls by job ID
 * @param {string} phoneNumber - Filter calls by phone number
 * @param {number} limit - Maximum number of calls to show initially
 * @param {boolean} showHeader - Whether to show the card header
 * @param {boolean} compact - Use compact view mode
 */
export default function CallHistoryTimeline({ 
  contactId, 
  jobId, 
  phoneNumber,
  limit = 5,
  showHeader = true,
  compact = false,
  className = ''
}) {
  const [calls, setCalls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const [selectedCall, setSelectedCall] = useState(null);
  const [totalCalls, setTotalCalls] = useState(0);

  const fetchCalls = useCallback(async () => {
    if (!contactId && !jobId && !phoneNumber) {
      setLoading(false);
      return;
    }
    
    try {
      const token = localStorage.getItem('access_token');
      const params = new URLSearchParams();
      params.append('page_size', expanded ? '50' : String(limit));
      
      if (contactId) params.append('contact_id', contactId);
      if (jobId) params.append('job_id', jobId);
      // Note: phone number search would need a backend endpoint update
      
      const response = await axios.get(`${API}/call-center/interactions?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setCalls(response.data.interactions || []);
      setTotalCalls(response.data.total || 0);
    } catch (err) {
      console.error('Failed to fetch call history:', err);
      // Don't show toast for embedded components
    } finally {
      setLoading(false);
    }
  }, [contactId, jobId, phoneNumber, limit, expanded]);

  useEffect(() => {
    fetchCalls();
  }, [fetchCalls]);

  const formatDuration = (seconds) => {
    if (!seconds) return '-';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatTime = (isoString) => {
    if (!isoString) return '-';
    const date = new Date(isoString);
    const now = new Date();
    const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) {
      return `Today at ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
    } else if (diffDays === 1) {
      return `Yesterday at ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
    } else if (diffDays < 7) {
      return date.toLocaleDateString([], { weekday: 'short', hour: '2-digit', minute: '2-digit' });
    } else {
      return date.toLocaleDateString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
    }
  };

  const getIcon = (call) => {
    if (call.status === 'missed') return PhoneMissed;
    if (call.status === 'voicemail') return Voicemail;
    if (call.direction === 'outbound') return PhoneOutgoing;
    return PhoneIncoming;
  };

  const displayCalls = expanded ? calls : calls.slice(0, limit);

  if (loading) {
    return (
      <Card className={className}>
        {showHeader && (
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Phone className="h-4 w-4" />
              Call History
            </CardTitle>
          </CardHeader>
        )}
        <CardContent>
          <div className="flex items-center justify-center py-4 text-muted-foreground">
            <RefreshCw className="h-4 w-4 animate-spin mr-2" />
            Loading...
          </div>
        </CardContent>
      </Card>
    );
  }

  if (calls.length === 0) {
    return (
      <Card className={className}>
        {showHeader && (
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Phone className="h-4 w-4" />
              Call History
            </CardTitle>
          </CardHeader>
        )}
        <CardContent>
          <div className="flex flex-col items-center justify-center py-6 text-muted-foreground">
            <Phone className="h-8 w-8 mb-2 opacity-30" />
            <p className="text-sm">No call history</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card className={className}>
        {showHeader && (
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2">
                <Phone className="h-4 w-4" />
                Call History
                {totalCalls > 0 && (
                  <Badge variant="secondary" className="ml-1">{totalCalls}</Badge>
                )}
              </CardTitle>
              <Button variant="ghost" size="sm" onClick={fetchCalls}>
                <RefreshCw className="h-3 w-3" />
              </Button>
            </div>
          </CardHeader>
        )}
        <CardContent className={compact ? 'pt-0' : ''}>
          <div className="relative">
            {/* Timeline line */}
            <div className="absolute left-4 top-0 bottom-0 w-px bg-border" />
            
            {/* Timeline items */}
            <div className="space-y-4">
              {displayCalls.map((call, index) => {
                const Icon = getIcon(call);
                const status = statusConfig[call.status] || statusConfig.completed;
                const region = regionConfig[call.region];
                
                return (
                  <div 
                    key={call.id}
                    className="relative pl-10 group cursor-pointer"
                    onClick={() => setSelectedCall(call)}
                  >
                    {/* Timeline dot */}
                    <div className={`absolute left-2 w-5 h-5 rounded-full flex items-center justify-center ${
                      call.status === 'missed' ? 'bg-red-100' : 
                      call.direction === 'inbound' ? 'bg-green-100' : 'bg-blue-100'
                    }`}>
                      <Icon className={`h-3 w-3 ${
                        call.status === 'missed' ? 'text-red-600' :
                        call.direction === 'inbound' ? 'text-green-600' : 'text-blue-600'
                      }`} />
                    </div>
                    
                    {/* Content */}
                    <div className={`p-3 rounded-lg border group-hover:bg-accent/50 transition-colors ${
                      compact ? 'py-2' : ''
                    }`}>
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <Badge className={`${status.color} text-xs`}>
                              {status.label}
                            </Badge>
                            <span className="text-xs text-muted-foreground">
                              {call.direction === 'inbound' ? 'Inbound' : 'Outbound'}
                            </span>
                            {call.recording_url && (
                              <Play className="h-3 w-3 text-muted-foreground" />
                            )}
                          </div>
                          
                          {!compact && (
                            <div className="flex items-center gap-2 mt-1 text-sm text-muted-foreground flex-wrap">
                              {call.tracking_number_name && (
                                <span className="truncate max-w-[200px]">
                                  {call.tracking_number_name}
                                </span>
                              )}
                              {call.source && (
                                <Badge variant="outline" className="text-xs">
                                  <Tag className="h-2 w-2 mr-1" />
                                  {call.source}
                                </Badge>
                              )}
                              {region && (
                                <Badge variant="outline" className={`text-xs ${region.color}`}>
                                  <MapPin className="h-2 w-2 mr-1" />
                                  {call.region}
                                </Badge>
                              )}
                            </div>
                          )}
                        </div>
                        
                        <div className="text-right shrink-0">
                          <div className="text-xs text-muted-foreground">
                            {formatTime(call.started_at)}
                          </div>
                          <div className="text-xs font-medium mt-1">
                            {formatDuration(call.duration_seconds)}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          
          {/* Show more/less button */}
          {totalCalls > limit && (
            <Button
              variant="ghost"
              className="w-full mt-4"
              onClick={(e) => {
                e.stopPropagation();
                setExpanded(!expanded);
              }}
            >
              {expanded ? (
                <>
                  <ChevronUp className="h-4 w-4 mr-2" />
                  Show Less
                </>
              ) : (
                <>
                  <ChevronDown className="h-4 w-4 mr-2" />
                  Show All {totalCalls} Calls
                </>
              )}
            </Button>
          )}
        </CardContent>
      </Card>

      {/* Call Detail Modal */}
      <Dialog open={!!selectedCall} onOpenChange={() => setSelectedCall(null)}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>Call Details</DialogTitle>
          </DialogHeader>
          {selectedCall && (
            <div className="space-y-4">
              {/* Header */}
              <div className="flex items-center gap-4 pb-4 border-b">
                <div className={`p-3 rounded-full ${
                  selectedCall.status === 'missed' ? 'bg-red-100' :
                  selectedCall.direction === 'inbound' ? 'bg-green-100' : 'bg-blue-100'
                }`}>
                  {React.createElement(getIcon(selectedCall), {
                    className: `h-6 w-6 ${
                      selectedCall.status === 'missed' ? 'text-red-600' :
                      selectedCall.direction === 'inbound' ? 'text-green-600' : 'text-blue-600'
                    }`
                  })}
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold">
                    {selectedCall.caller_name || selectedCall.contact_name || selectedCall.caller_number}
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    {selectedCall.tracking_number_name || selectedCall.tracking_number}
                  </p>
                </div>
                <Badge className={statusConfig[selectedCall.status]?.color}>
                  {statusConfig[selectedCall.status]?.label || selectedCall.status}
                </Badge>
              </div>

              {/* Details Grid */}
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <label className="text-muted-foreground">Direction</label>
                  <p className="font-medium capitalize">{selectedCall.direction}</p>
                </div>
                <div>
                  <label className="text-muted-foreground">Duration</label>
                  <p className="font-medium">{formatDuration(selectedCall.duration_seconds)}</p>
                </div>
                <div>
                  <label className="text-muted-foreground">Date & Time</label>
                  <p className="font-medium">{new Date(selectedCall.started_at).toLocaleString()}</p>
                </div>
                <div>
                  <label className="text-muted-foreground">Caller Number</label>
                  <p className="font-medium">{selectedCall.caller_number || '-'}</p>
                </div>
                {selectedCall.source && (
                  <div>
                    <label className="text-muted-foreground">Source</label>
                    <Badge variant="secondary">{selectedCall.source}</Badge>
                  </div>
                )}
                {selectedCall.region && (
                  <div>
                    <label className="text-muted-foreground">Region</label>
                    <Badge variant="outline" className={regionConfig[selectedCall.region]?.color}>
                      {regionConfig[selectedCall.region]?.name || selectedCall.region}
                    </Badge>
                  </div>
                )}
                {selectedCall.campaign && (
                  <div>
                    <label className="text-muted-foreground">Campaign</label>
                    <p className="font-medium">{selectedCall.campaign}</p>
                  </div>
                )}
              </div>

              {/* Recording */}
              {selectedCall.recording_url && (
                <div className="pt-4 border-t">
                  <label className="text-sm text-muted-foreground">Recording</label>
                  <audio controls className="w-full mt-2">
                    <source src={selectedCall.recording_url} />
                  </audio>
                </div>
              )}

              {/* Transcript */}
              {selectedCall.transcript && (
                <div className="pt-4 border-t">
                  <label className="text-sm text-muted-foreground">Transcript</label>
                  <p className="text-sm bg-muted p-3 rounded mt-2 max-h-32 overflow-y-auto">
                    {selectedCall.transcript}
                  </p>
                </div>
              )}

              {selectedCall.is_backfill && (
                <p className="text-xs text-center text-muted-foreground">
                  This call was imported from historical CallRail data
                </p>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setSelectedCall(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
